import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const [error, setError] = useState(null);
  
  console.log('🚀 OAUTH DEBUG: AuthCallback component mounted');
  console.log('🚀 OAUTH DEBUG: Current URL:', window.location.href);
  console.log('🚀 OAUTH DEBUG: location.search:', location.search);
  console.log('🚀 OAUTH DEBUG: location.pathname:', location.pathname);
  console.log('🚀 OAUTH DEBUG: location.hash:', location.hash);
  console.log('🚀 OAUTH DEBUG: User state:', { 
    hasUser: !!user, 
    userEmail: user?.email,
    loading,
    onboardingCompleted: user?.profile?.onboarding_completed
  });

  // Skip the complex callback flow and just wait for auth state
  useEffect(() => {
    console.log('🚀 OAUTH DEBUG: Simplified callback - just waiting for auth state');
  }, []);
  
  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        const error_param = urlParams.get('error');

        // Handle OAuth error
        if (error_param) {
          setError(`OAuth error: ${error_param}`);
          setTimeout(() => navigate('/login', { replace: true }), 3000);
          return;
        }

        // Check if session already exists (Supabase auto-handles PKCE on page load)
        const { data: sessionData } = await supabase.auth.getSession();

        if (sessionData?.session) {
          // Session already established by Supabase's automatic PKCE handling
          // Clean up URL and let auth context handle navigation
          window.history.replaceState({}, document.title, '/auth/callback');
          return;
        }

        // Only attempt manual code exchange if no session exists
        if (code && !sessionData?.session) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);

          if (error) {
            // Check if error is due to code already being used (expected in auto-handled PKCE)
            if (error.message.includes('code') || error.message.includes('verifier')) {
              // Session will be picked up by auth context, ignore this error
              return;
            }

            setError(`Authentication failed: ${error.message}`);
            setTimeout(() => navigate('/login', { replace: true }), 3000);
            return;
          }

          if (data?.session) {
            // Clean up URL
            window.history.replaceState({}, document.title, '/auth/callback');
            return;
          }
        }

        // Fallback: wait for auth context to resolve
        setTimeout(() => {
          if (!user) {
            navigate('/login', { replace: true });
          }
        }, 5000);

      } catch (err) {
        setError(`Authentication failed: ${err.message}`);
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    // Only handle callback if there are auth-related URL parameters
    const urlParams = new URLSearchParams(location.search);
    const hasAuthParams = urlParams.get('code') || location.hash.includes('access_token');

    if (hasAuthParams) {
      handleAuthCallback();
    }
  }, [location.search, location.hash, navigate, user]);

  useEffect(() => {
    console.log('🔥 OAUTH DEBUG: Navigation effect triggered');
    console.log('🔥 OAUTH DEBUG: Navigation state:', {
      loading,
      hasUser: !!user,
      userEmail: user?.email,
      hasProfile: !!user?.profile,
      onboardingCompleted: user?.profile?.onboarding_completed,
      error
    });
    
    // Navigate based on authentication and profile state
    if (!loading) {
      console.log('🔥 OAUTH DEBUG: Loading complete, checking navigation...');
      
      if (user?.profile?.onboarding_completed) {
        console.log('🔥 OAUTH DEBUG: User onboarded, navigating to dashboard');
        navigate('/dashboard', { replace: true });
      } else if (user?.profile && !user.profile.onboarding_completed) {
        console.log('🔥 OAUTH DEBUG: User not onboarded, navigating to onboarding');
        navigate('/onboarding/welcome', { replace: true });
      } else if (!user && !error) {
        console.log('🔥 OAUTH DEBUG: No user and no error, will redirect to login after timeout');
        setTimeout(() => {
          console.log('🔥 OAUTH DEBUG: Timeout reached, redirecting to login');
          navigate('/login', { replace: true });
        }, 2000);
      } else {
        console.log('🔥 OAUTH DEBUG: Waiting for user state to resolve...');
      }
    } else {
      console.log('🔥 OAUTH DEBUG: Still loading, waiting...');
    }
  }, [user, loading, navigate, error]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="spinner spinner-lg mx-auto mb-4"></div>
        <p className="text-gray-900 font-medium">
          {error ? 'Sign in failed' : 'Completing sign in...'}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          {error ? `Error: ${error}` : 'This should only take a moment'}
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
